const GK_PURPLE = '#1f2937';
const FONT = 'Inter,system-ui,sans-serif';

/**
 * Returns a scale factor (0..1) for markers based on the current map zoom level.
 * At zoom 7 or below markers are at minimum size (0.35), at zoom 15+ they are full size (1.0).
 * Between 7 and 15 the scale grows linearly.
 */
export const getZoomScale = (zoom: number): number => {
  const MIN_ZOOM = 7;
  const MAX_ZOOM = 15;
  const MIN_SCALE = 0.35;
  const MAX_SCALE = 1.0;
  if (zoom <= MIN_ZOOM) return MIN_SCALE;
  if (zoom >= MAX_ZOOM) return MAX_SCALE;
  return MIN_SCALE + (MAX_SCALE - MIN_SCALE) * ((zoom - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM));
};

let anonymousStoreIconCache: string | null = null;

/**
 * Returns a data URL for a simple storefront SVG icon.
 * Used as the "logo" for anonymous neighbourhood markers.
 */
export const getAnonymousStoreIcon = (): string => {
  if (anonymousStoreIconCache) return anonymousStoreIconCache;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="48" height="48" fill="none" stroke="#374151" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
<rect x="14" y="22" width="20" height="14" rx="1"/>
<line x1="14" y1="25" x2="34" y2="25"/>
<line x1="14" y1="22" x2="34" y2="22"/>
<path d="M14 22l3-7h14l3 7"/>
<rect x="20" y="29" width="8" height="7"/>
</svg>`;

  anonymousStoreIconCache = `data:image/svg+xml;base64,${btoa(svg)}`;
  return anonymousStoreIconCache;
};

const stateClusterCache = new Map<string, string>();
const regionClusterCache = new Map<string, string>();

const formatCount = (count: number): string => {
  if (count >= 10000) return `${(count / 1000).toFixed(0)}k`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return `${count}`;
};

export const createStateClusterMarker = (stateCode: string, count: number, customers: number): string => {
  const cacheKey = `${stateCode}|${count}`;
  if (stateClusterCache.has(cacheKey)) return stateClusterCache.get(cacheKey)!;

  const color = GK_PURPLE;
  const label = formatCount(count);
  const s = 60;
  const cx = s / 2;
  const cy = s / 2;
  const r = 24;

  const svg = `<svg width="${s}" height="${s}" viewBox="0 0 ${s} ${s}" xmlns="http://www.w3.org/2000/svg">
<circle cx="${cx}" cy="${cy}" r="${r + 4}" fill="${color}" opacity="0.08"/>
<circle cx="${cx}" cy="${cy}" r="${r}" fill="white" stroke="${color}" stroke-width="2.5"/>
<text x="${cx}" y="${cy - 4}" text-anchor="middle" dominant-baseline="middle" font-family="${FONT}" font-weight="700" font-size="12" fill="${color}" letter-spacing="0.5">${stateCode}</text>
<text x="${cx}" y="${cy + 9}" text-anchor="middle" dominant-baseline="middle" font-family="${FONT}" font-weight="600" font-size="9" fill="${color}" opacity="0.7">${label}</text>
</svg>`;

  const result = `data:image/svg+xml;base64,${btoa(svg)}`;
  stateClusterCache.set(cacheKey, result);
  return result;
};

export const createRegionClusterIcon = (count: number, isCustomerMajority: boolean): string => {
  const key = `${count}`;
  if (regionClusterCache.has(key)) return regionClusterCache.get(key)!;

  const color = GK_PURPLE;
  const label = formatCount(count);
  const s = 48;
  const cx = s / 2;
  const cy = s / 2;
  const r = 19;

  const svg = `<svg width="${s}" height="${s}" viewBox="0 0 ${s} ${s}" xmlns="http://www.w3.org/2000/svg">
<circle cx="${cx}" cy="${cy}" r="${r + 3}" fill="${color}" opacity="0.08"/>
<circle cx="${cx}" cy="${cy}" r="${r}" fill="${color}" stroke="white" stroke-width="2"/>
<text x="${cx}" y="${cy + 1}" text-anchor="middle" dominant-baseline="middle" font-family="${FONT}" font-weight="700" font-size="${count >= 1000 ? 9 : 11}" fill="white">${label}</text>
</svg>`;

  const result = `data:image/svg+xml;base64,${btoa(svg)}`;
  regionClusterCache.set(key, result);
  return result;
};

const simpleMarkerCache = new Map<string, string>();
const detailedMarkerCache = new Map<string, string>();
const storesMapIconCache = new Map<string, string>();

export const createSimpleMarker = (isCustomer: boolean): string => {
  const cacheKey = `${isCustomer}`;
  if (simpleMarkerCache.has(cacheKey)) return simpleMarkerCache.get(cacheKey)!;

  const markerColor = isCustomer ? '#003da6' : '#EA0B0B';
  const svg = `<svg width="12" height="12" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg"><circle cx="6" cy="6" r="6" fill="${markerColor}" opacity="0.9"/></svg>`;
  const result = `data:image/svg+xml;base64,${btoa(svg)}`;
  simpleMarkerCache.set(cacheKey, result);
  return result;
};

const getDetailedMarkerStyles = (color: string): string => `
  .outer{fill:white;stroke:${color};stroke-width:2.5}
  .inner{fill:white}
  .badge{fill:white;stroke:${color};stroke-width:1.2}
  .badge-txt{font:700 8px Arial,sans-serif;fill:${color};text-anchor:middle;dominant-baseline:middle}
  .brand-txt{font:900 14px Arial,sans-serif;fill:#111827;text-anchor:middle;dominant-baseline:middle}
  .ref{fill:#9333ea}
  .ref-txt{font:700 9px Arial,sans-serif;fill:white;text-anchor:middle;dominant-baseline:middle}
`;

export const createDetailedMarker = (
  brandInitial: string,
  isCustomer: boolean,
  logoDataUrl: string = '',
  labels: string[] = [],
  isReference: boolean = false
): string => {
  const cacheKey = `${brandInitial}|${isCustomer}|${labels.join(',')}|${isReference}|${logoDataUrl}`;
  if (detailedMarkerCache.has(cacheKey)) return detailedMarkerCache.get(cacheKey)!;

  const markerColor = isCustomer ? '#003da6' : '#EA0B0B';
  const hasBadges = labels.length > 0;

  const badgeText = labels.join(' ');
  const charWidth = 5.5;
  const padding = 16;
  const minBadgeWidth = 45;
  const badgeWidth = Math.min(Math.max(minBadgeWidth, badgeText.length * charWidth + padding), 74);
  const badgeLeft = (80 - badgeWidth) / 2;

  const svgHeight = hasBadges ? 108 : 80;
  const centerY = hasBadges ? 50 : 40;
  const centerX = 40;
  const outerRadius = 19.5;
  const innerRadius = 16;
  const logoRadius = 15.5;
  const clipPathId = 'lc';

  let svg = `<svg width="80" height="${svgHeight}" viewBox="0 0 80 ${svgHeight}" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">`;
  svg += `<style>${getDetailedMarkerStyles(markerColor)}</style>`;

  if (hasBadges) {
    svg += `<rect class="badge" x="${badgeLeft}" y="10" width="${badgeWidth}" height="20" rx="10" ry="10"/>`;
    svg += `<text class="badge-txt" x="40" y="22">${badgeText}</text>`;
  }

  svg += `<defs><clipPath id="${clipPathId}"><circle cx="${centerX}" cy="${centerY}" r="${logoRadius}"/></clipPath></defs>`;
  svg += `<circle class="outer" cx="${centerX}" cy="${centerY}" r="${outerRadius}"/>`;
  svg += `<circle class="inner" cx="${centerX}" cy="${centerY}" r="${innerRadius}"/>`;

  if (logoDataUrl) {
    const imageSize = logoRadius * 2.8;
    const imageOffset = imageSize / 2;
    svg += `<image xlink:href="${logoDataUrl}" x="${centerX - imageOffset}" y="${centerY - imageOffset}" width="${imageSize}" height="${imageSize}" clip-path="url(#${clipPathId})" preserveAspectRatio="xMidYMid slice"/>`;
  } else {
    svg += `<text class="brand-txt" x="${centerX}" y="${centerY + 1}">${brandInitial}</text>`;
  }

  if (isReference) {
    const refBadgeY = hasBadges ? svgHeight - 25 : svgHeight - 20;
    svg += `<rect class="ref" x="20" y="${refBadgeY}" width="40" height="20" rx="10" ry="10"/>`;
    svg += `<text class="ref-txt" x="40" y="${refBadgeY + 10}">REF</text>`;
  }

  svg += `</svg>`;
  const result = `data:image/svg+xml;base64,${btoa(svg)}`;
  detailedMarkerCache.set(cacheKey, result);
  return result;
};

const pulseMarkerCache = new Map<string, string>();

export const createDetailedMarkerWithPulse = (
  brandInitial: string,
  isCustomer: boolean,
  logoDataUrl: string = '',
  labels: string[] = [],
): string => {
  const cacheKey = `pulse|${brandInitial}|${isCustomer}|${labels.join(',')}|${logoDataUrl}`;
  if (pulseMarkerCache.has(cacheKey)) return pulseMarkerCache.get(cacheKey)!;

  const markerColor = isCustomer ? '#003da6' : '#EA0B0B';
  const pulseColor = '#003da6';
  const hasBadges = labels.length > 0;

  const badgeText = labels.join(' ');
  const charWidth = 5.5;
  const padding = 16;
  const minBadgeWidth = 45;
  const badgeWidth = Math.min(Math.max(minBadgeWidth, badgeText.length * charWidth + padding), 74);

  // Extra padding for pulse rings (16px expansion on each side)
  const pulseExtra = 20;
  const svgBaseHeight = hasBadges ? 108 : 80;
  const svgWidth = 80 + pulseExtra * 2;
  const svgHeight = svgBaseHeight + pulseExtra * 2;
  const centerY = (hasBadges ? 50 : 40) + pulseExtra;
  const centerX = 40 + pulseExtra;
  const outerRadius = 19.5;
  const innerRadius = 16;
  const logoRadius = 15.5;
  const clipPathId = 'lc';

  let svg = `<svg width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">`;
  svg += `<style>${getDetailedMarkerStyles(markerColor)}</style>`;

  // Pulse rings animation
  svg += `<circle cx="${centerX}" cy="${centerY}" r="${outerRadius}" fill="none" stroke="${pulseColor}" stroke-width="2" opacity="0">`;
  svg += `<animate attributeName="r" from="${outerRadius}" to="${outerRadius + 16}" dur="2s" repeatCount="indefinite"/>`;
  svg += `<animate attributeName="opacity" from="0.8" to="0" dur="2s" repeatCount="indefinite"/>`;
  svg += `</circle>`;
  svg += `<circle cx="${centerX}" cy="${centerY}" r="${outerRadius}" fill="none" stroke="${pulseColor}" stroke-width="1.5" opacity="0">`;
  svg += `<animate attributeName="r" from="${outerRadius}" to="${outerRadius + 12}" dur="2s" begin="0.6s" repeatCount="indefinite"/>`;
  svg += `<animate attributeName="opacity" from="0.6" to="0" dur="2s" begin="0.6s" repeatCount="indefinite"/>`;
  svg += `</circle>`;

  if (hasBadges) {
    svg += `<rect class="badge" x="${(svgWidth - badgeWidth) / 2}" y="${10 + pulseExtra}" width="${badgeWidth}" height="20" rx="10" ry="10"/>`;
    svg += `<text class="badge-txt" x="${centerX}" y="${22 + pulseExtra}">${badgeText}</text>`;
  }

  svg += `<defs><clipPath id="${clipPathId}"><circle cx="${centerX}" cy="${centerY}" r="${logoRadius}"/></clipPath></defs>`;
  svg += `<circle class="outer" cx="${centerX}" cy="${centerY}" r="${outerRadius}"/>`;
  svg += `<circle class="inner" cx="${centerX}" cy="${centerY}" r="${innerRadius}"/>`;

  if (logoDataUrl) {
    const imageSize = logoRadius * 2.8;
    const imageOffset = imageSize / 2;
    svg += `<image xlink:href="${logoDataUrl}" x="${centerX - imageOffset}" y="${centerY - imageOffset}" width="${imageSize}" height="${imageSize}" clip-path="url(#${clipPathId})" preserveAspectRatio="xMidYMid slice"/>`;
  } else {
    svg += `<text class="brand-txt" x="${centerX}" y="${centerY + 1}">${brandInitial}</text>`;
  }

  svg += `</svg>`;
  const result = `data:image/svg+xml;base64,${btoa(svg)}`;
  pulseMarkerCache.set(cacheKey, result);
  return result;
};

export const getMarkerScaledSize = (
  isDetailed: boolean,
  svgHeight: number = 80
): { width: number; height: number } => {
  if (isDetailed) {
    return { width: 80, height: svgHeight };
  }
  return { width: 12, height: 12 };
};

export const getPulseMarkerScaledSize = (
  svgHeight: number = 80
): { width: number; height: number } => {
  const pulseExtra = 20;
  return { width: 80 + pulseExtra * 2, height: svgHeight + pulseExtra * 2 };
};

export const getMarkerAnchor = (
  isDetailed: boolean,
  hasLabels: boolean = false
): { x: number; y: number } => {
  if (isDetailed) {
    return { x: 40, y: hasLabels ? 50 : 40 };
  }
  return { x: 6, y: 6 };
};

export const getPulseMarkerAnchor = (
  hasLabels: boolean = false
): { x: number; y: number } => {
  const pulseExtra = 20;
  return { x: 40 + pulseExtra, y: (hasLabels ? 54 : 40) + pulseExtra };
};

const getStoresMapIconStyles = (color: string): string => `
  .glow{fill:white;opacity:0.1}
  .outer{fill:${color};stroke:white;stroke-width:2}
  .inner{fill:white}
  .badge{fill:white;stroke:${color};stroke-width:1.2}
  .badge-txt{font:700 8px Arial,sans-serif;fill:${color};text-anchor:middle;dominant-baseline:middle}
  .brand-txt{font:900 14px Arial,sans-serif;fill:white;text-anchor:middle;dominant-baseline:middle}
  .ref{fill:#9333ea}
  .ref-txt{font:700 9px Arial,sans-serif;fill:white;text-anchor:middle;dominant-baseline:middle}
`;

export const createCustomStoresMapIcon = (
  labels: string[] = [],
  isReference: boolean = false,
  hasAnySolution: boolean = false,
  logoDataUrl: string = '',
  brandInitial: string = 'K'
): string => {
  const cacheKey = `${labels.join(',')}|${isReference}|${hasAnySolution}|${brandInitial}|${logoDataUrl}`;
  if (storesMapIconCache.has(cacheKey)) return storesMapIconCache.get(cacheKey)!;

  const markerColor = isReference ? '#003da6' : hasAnySolution ? '#003da6' : '#EA0B0B';
  const hasBadges = labels.length > 0;

  const badgeText = labels.join(' ');
  const charWidth = 5.5;
  const padding = 16;
  const minBadgeWidth = 45;
  const badgeWidth = Math.min(Math.max(minBadgeWidth, badgeText.length * charWidth + padding), 74);
  const badgeLeft = (80 - badgeWidth) / 2;

  const svgWidth = 80;
  const svgHeight = isReference ? (hasBadges ? 138 : 110) : hasBadges ? 108 : 80;
  const centerX = 40;
  const centerY = isReference ? (hasBadges ? 50 : 55) : hasBadges ? 50 : 40;
  const outerRadius = 19.5;
  const innerRadius = 16;
  const logoRadius = 14;
  const clipPathId = 'lc';

  let svg = `<svg width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">`;
  svg += `<style>${getStoresMapIconStyles(markerColor)}</style>`;

  if (hasBadges) {
    svg += `<rect class="badge" x="${badgeLeft}" y="10" width="${badgeWidth}" height="20" rx="10" ry="10"/>`;
    svg += `<text class="badge-txt" x="40" y="22">${badgeText}</text>`;
  }

  svg += `<defs><clipPath id="${clipPathId}"><circle cx="${centerX}" cy="${centerY}" r="${logoRadius}"/></clipPath></defs>`;
  svg += `<circle class="glow" cx="${centerX}" cy="${centerY}" r="${outerRadius + 2}"/>`;
  svg += `<circle class="outer" cx="${centerX}" cy="${centerY}" r="${outerRadius}"/>`;
  svg += `<circle class="inner" cx="${centerX}" cy="${centerY}" r="${innerRadius}"/>`;

  if (logoDataUrl) {
    const imageSize = logoRadius * 2.8;
    const imageOffset = imageSize / 2;
    svg += `<image xlink:href="${logoDataUrl}" x="${centerX - imageOffset}" y="${centerY - imageOffset}" width="${imageSize}" height="${imageSize}" clip-path="url(#${clipPathId})" preserveAspectRatio="xMidYMid slice"/>`;
  } else {
    svg += `<text class="brand-txt" x="${centerX}" y="${centerY + 1}">${brandInitial}</text>`;
  }

  if (isReference) {
    const refBadgeY = hasBadges ? svgHeight - 40 : svgHeight - 20;
    svg += `<rect class="ref" x="20" y="${refBadgeY}" width="40" height="22" rx="12" ry="12"/>`;
    svg += `<text class="ref-txt" x="40" y="${refBadgeY + 11}">REF</text>`;
  }

  svg += `</svg>`;
  const result = `data:image/svg+xml;base64,${btoa(svg)}`;
  storesMapIconCache.set(cacheKey, result);
  return result;
};
